/**
 * Verify a self-registrant — link this person to an entity, or create one
 * (FR-B4). Split out of `People.tsx` and put on the kit's fields in Wave 15B
 * (C14 §10.6 item 7; T-6.14); what it sends is unchanged.
 *
 * Exactly one of the two, because they are different acts and a request that
 * gives both has not decided which it meant — the service refuses that, and so
 * does this dialog by making it a choice rather than two optional fields.
 */

import { Button, Dialog, Input, RadioGroup, Select } from '@fiveox/ui'
import { UserPlus } from 'lucide-react'
import { useEffect, useState } from 'react'

import { DialogActions, FORM_GRID, FormRow } from '../../components/page'
import { ApiError } from '../../lib/api'
import { type ClientRecord, clients } from '../../lib/api/clients'
import { type QueueRow, persons } from '../../lib/api/persons'

export function VerifyDialog({
  row,
  onClose,
  onDone,
}: {
  row: QueueRow
  onClose: () => void
  onDone: () => void
}) {
  const claimed = row.submitted_details ?? {}
  const [mode, setMode] = useState<'link' | 'create'>(row.match ? 'link' : 'create')
  const [clientId, setClientId] = useState(row.match?.client_id ?? '')
  const [options, setOptions] = useState<ClientRecord[]>([])
  const [legalName, setLegalName] = useState(String(claimed.legal_name ?? row.full_name))
  const [kind, setKind] = useState(String(claimed.kind ?? 'individual'))
  const [pan, setPan] = useState(String(claimed.pan ?? ''))
  const [error, setError] = useState<string | null>(null)
  const [attempted, setAttempted] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    clients
      .list({ limit: 200 })
      .then((body) => setOptions(body.data))
      .catch(() => setOptions([]))
  }, [])

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    // #13: *Verify* is pressable while nothing is being sent; pressing it with
    // no client chosen says so under the field rather than greying out.
    setAttempted(true)
    if (!ready) return
    setSaving(true)
    setError(null)
    try {
      await persons.verify(
        row.id,
        mode === 'link'
          ? { client_id: clientId }
          : {
              entity: {
                legal_name: legalName.trim(),
                kind,
                // A business needs a constitution; this dialog only creates
                // individuals and leaves anything else to the full client form,
                // where the constitution and the company type are asked for
                // properly. Guessing one from a web form would put a wrong
                // constitution on the compliance calendar (FR-D2).
                constitution: kind === 'business' ? 'other' : null,
                pan: pan.trim().toUpperCase() || null,
              },
            },
      )
      onDone()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That could not be saved.')
    } finally {
      setSaving(false)
    }
  }

  const ready = mode === 'link' ? clientId !== '' : legalName.trim() !== ''

  return (
    <Dialog open onClose={onClose} title={`Verify ${row.full_name}`} error={error} width="lg">
      <form onSubmit={submit} noValidate>
        <RadioGroup
          legend="Link them to a client you already have, or create the client now."
          className="mb-5 sm:flex-row sm:gap-6"
          value={mode}
          onChange={setMode}
          options={[
            {
              value: 'link',
              label: 'Link to an existing client',
              description: row.match ? `Their PAN matches ${row.match.legal_name}.` : undefined,
            },
            { value: 'create', label: 'Create the client' },
          ]}
        />

        <div className={FORM_GRID}>
          {mode === 'link' ? (
            <FormRow label="Client" htmlFor="verify-client" required>
              <Select
                id="verify-client"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                required
                aria-required="true"
                error={attempted && !clientId ? 'Choose the client to link them to.' : null}
                hint="They’ll be linked as authorised. You can make them an owner afterwards from the client’s page."
              >
                <option value="" disabled hidden>
                  Choose…
                </option>
                {options.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.legal_name}
                  </option>
                ))}
              </Select>
            </FormRow>
          ) : (
            <>
              <FormRow label="Legal name" htmlFor="verify-legal-name" required>
                <Input
                  id="verify-legal-name"
                  value={legalName}
                  onChange={(e) => setLegalName(e.target.value)}
                  required
                  aria-required="true"
                  error={attempted && !legalName.trim() ? 'A client needs a name.' : null}
                />
              </FormRow>
              <FormRow label="Kind">
                <RadioGroup
                  legend="Kind"
                  hideLegend
                  className="sm:pt-3"
                  value={kind as 'individual' | 'business'}
                  onChange={setKind}
                  options={[
                    {
                      value: 'individual',
                      label: 'Individual',
                      description: 'They’ll be the owner of their own record.',
                    },
                    { value: 'business', label: 'Business' },
                  ]}
                />
              </FormRow>
              <FormRow label="PAN" htmlFor="verify-pan" optional>
                <Input
                  id="verify-pan"
                  value={pan}
                  onChange={(e) => setPan(e.target.value)}
                  maxLength={10}
                  className="font-mono uppercase"
                />
              </FormRow>
            </>
          )}
        </div>

        <DialogActions>
          <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" loading={saving} loadingText="Saving…">
            <UserPlus size={16} aria-hidden /> Verify
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
